// Settings (admin): paste the Apollo and Apify keys (stored server-side, never shown again) and the daily cap.
import { useQueryClient } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { useState } from "react";
import { NumberInput } from "@/components/admin/number-input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

import type { CrmDataProvider } from "../../providers/types";
import { useEngineStatus } from "./leads";

const ENGINES = [
  {
    engine: "apollo" as const,
    label: "Apollo.io",
    help: "https://app.apollo.io/#/settings/integrations/api",
  },
  {
    engine: "apify" as const,
    label: "Apify",
    help: "https://console.apify.com/settings/integrations",
  },
];

/** withCap: the daily cap is a field of the desktop Settings form; the mobile Settings page has no such form */
export const LeadEngineSettings = ({
  withCap = true,
}: {
  withCap?: boolean;
}) => {
  const translate = useTranslate();
  const { data: status = [] } = useEngineStatus();
  return (
    <Card id="lead-engines">
      <CardContent className="space-y-4">
        <h2 className="text-xl font-semibold text-muted-foreground">
          {translate("resources.leads.engines.title")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {translate("resources.leads.engines.help")}
        </p>
        {ENGINES.map((e) => (
          <EngineKeyRow
            key={e.engine}
            {...e}
            status={status.find((s) => s.engine === e.engine)}
          />
        ))}
        {withCap ? (
          <NumberInput
            source="lead_engine_daily_cap"
            label="resources.leads.engines.daily_cap"
            helperText="resources.leads.engines.daily_cap_help"
            min={1}
            max={10000}
          />
        ) : null}
      </CardContent>
    </Card>
  );
};

const EngineKeyRow = ({
  engine,
  label,
  help,
  status,
}: (typeof ENGINES)[number] & {
  status?: { has_key: boolean; updated_at: string | null };
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (value: string) => {
    setBusy(true);
    try {
      await dataProvider.setEngineKey(engine, value);
      setKey("");
      await queryClient.invalidateQueries({ queryKey: ["lead_engines"] });
      notify("resources.leads.engines.saved", { type: "success" });
    } catch (error) {
      notify((error as Error).message, { type: "error" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <KeyRound className="size-4" />
        <a
          href={help}
          target="_blank"
          rel="noreferrer"
          className="font-medium underline-offset-2 hover:underline"
        >
          {label}
        </a>
        {status?.has_key ? (
          <Badge variant="secondary">
            {translate("resources.leads.engines.set", {
              date: status.updated_at
                ? new Date(status.updated_at).toLocaleDateString()
                : "",
            })}
          </Badge>
        ) : (
          <Badge variant="outline">
            {translate("resources.leads.engines.not_set")}
          </Badge>
        )}
      </div>
      <div className="flex gap-2">
        {/* never filled from the server: the key cannot be read back */}
        <Input
          type="password"
          autoComplete="off"
          aria-label={translate("resources.leads.engines.paste", {
            engine: label,
          })}
          placeholder={translate("resources.leads.engines.paste", {
            engine: label,
          })}
          value={key}
          onChange={(e) => setKey(e.target.value)}
        />
        <Button
          type="button"
          disabled={busy || !key.trim()}
          onClick={() => save(key)}
        >
          {translate("ra.action.save")}
        </Button>
        {status?.has_key && (
          <Button
            type="button"
            variant="ghost"
            disabled={busy}
            onClick={() => save("")}
          >
            {translate("ra.action.remove")}
          </Button>
        )}
      </div>
    </div>
  );
};
